// Formula Functions Helper

// Basic formulas organized by category
export const BASIC_FORMULAS = {
  "Math": ["SUM", "AVERAGE", "MAX", "MIN", "ROUND", "ROUNDUP", "ROUNDDOWN", "ABS", "SQRT", "POWER"],
  "Logical": ["IF", "AND", "OR", "NOT"],
  "Text": ["CONCATENATE", "LEFT", "RIGHT", "LEN", "UPPER", "LOWER", "TRIM"],
};

// Get all basic formulas in a flat array
export const getAllBasicFormulas = () => {
  const formulas = [];
  Object.values(BASIC_FORMULAS).forEach(funcList => {
    formulas.push(...funcList);
  });
  return formulas.sort();
};

// Get function syntax/description
export const getFunctionSyntax = (funcName) => {
  const syntaxMap = {
    "SUM": "SUM(number1, number2, ...)",
    "AVERAGE": "AVERAGE(number1, number2, ...)",
    "MAX": "MAX(number1, number2, ...)",
    "MIN": "MIN(number1, number2, ...)",
    "ROUND": "ROUND(number, num_digits)",
    "ROUNDUP": "ROUNDUP(number, num_digits)",
    "ROUNDDOWN": "ROUNDDOWN(number, num_digits)",
    "ABS": "ABS(number)",
    "SQRT": "SQRT(number)",
    "POWER": "POWER(number, power)",
    "IF": "IF(condition, value_if_true, value_if_false)",
    "AND": "AND(logical1, logical2, ...)",
    "OR": "OR(logical1, logical2, ...)",
    "NOT": "NOT(logical)",
    "CONCATENATE": "CONCATENATE(text1, text2, ...)",
    "LEFT": "LEFT(text, num_chars)",
    "RIGHT": "RIGHT(text, num_chars)",
    "LEN": "LEN(text)",
    "UPPER": "UPPER(text)",
    "LOWER": "LOWER(text)",
    "TRIM": "TRIM(text)",
  };
  
  return syntaxMap[funcName] || `${funcName}(...)`;
};

/**
 * SQL keywords and function names that should be skipped when extracting column references
 */
const SQL_KEYWORDS = new Set([
  "SELECT", "FROM", "WHERE", "GROUP", "BY", "ORDER", "HAVING", "JOIN",
  "INNER", "LEFT", "RIGHT", "FULL", "OUTER", "ON", "AS", "AND", "OR", "NOT",
  "IN", "IS", "NULL", "LIKE", "BETWEEN", "EXISTS", "CASE", "WHEN", "THEN",
  "ELSE", "END", "SUM", "AVG", "MAX", "MIN", "COUNT", "ROUND", "CEIL", "FLOOR",
  "ABS", "SQRT", "POWER", "CONCAT", "LEFT", "RIGHT", "LENGTH", "UPPER", "LOWER",
  "TRIM", "CAST", "CONVERT", "DATE", "TIME", "YEAR", "MONTH", "DAY", "TRUE", "FALSE",
  "IF", "CONCATENATE", "AVERAGE","LEAST","GREATEST","COALESCE","NULLIF"
]);

/**
 * Checks if a position in a string is inside quotes
 */
const isInsideQuotes = (str, pos) => {
  const before = str.substring(0, pos);
  const singleQuotes = (before.match(/'/g) || []).length;
  const doubleQuotes = (before.match(/"/g) || []).length;
  return singleQuotes % 2 !== 0 || doubleQuotes % 2 !== 0;
};

/**
 * Gets columns from localStorage schema data for a specific table
 * Structure: postgres_tables_data/snowflake_tables_data -> [database] -> children -> [table] -> children -> [column]
 *
 * When withTypes=false returns array of column names (strings).
 * When withTypes=true returns array of objects: { name, textDatatype }.
 *
 * @param {string} tableName - The table name to find
 * @param {boolean} isPostgres - Whether to use postgres_tables_data or snowflake_tables_data
 * @param {boolean} withTypes - Whether to include datatype metadata
 * @returns {Array<string>|Array<{name: string, textDatatype: string}>}
 */
const getColumnsFromSchemaData = (tableName, isPostgres = true, withTypes = false) => {
  try {
    const storageKey = isPostgres ? "postgres_tables_data" : "snowflake_tables_data";
    const tablesDataStr = localStorage.getItem(storageKey);
    
    if (!tablesDataStr) return [];
    
    const tablesData = JSON.parse(tablesDataStr);
    if (!Array.isArray(tablesData)) return [];
    
    // Traverse the structure: database -> children -> table -> children -> column
    for (const database of tablesData) {
      if (database.children && Array.isArray(database.children)) {
        for (const table of database.children) {
          if (table.type === "Table" && table.name === tableName) {
            // Found the table, now get its columns
            if (table.children && Array.isArray(table.children)) {
              const cols = table.children.filter(
                (col) => col.type === "column" && col.name
              );
              if (withTypes) {
                return cols.map((col) => ({
                  name: col.name,
                  textDatatype: col.textDatatype || "",
                }));
              }
              return cols.map((col) => col.name);
            }
          }
        }
      }
    }
    
    return [];
  } catch (error) {
    console.error("Error getting columns from schema data:", error);
    return [];
  }
};

/**
 * Extracts column references from a formula
 * Returns an array of column names (normalized with underscores) that are referenced in the formula
 * 
 * @param {string} formula - The Excel formula (e.g., "=CONCATENATE(QUARTER_NM,WEEK_NM)")
 * @param {string} tableName - The table name to check for column existence (optional)
 * @param {boolean} isPostgres - Whether to use postgres_tables_data or snowflake_tables_data (optional)
 * @returns {Array<string>} - Array of column names referenced in the formula
 */
export const extractColumnReferencesFromFormula = (formula, tableName = null, isPostgres = true) => {
  if (!formula || typeof formula !== "string") return [];
  
  let formulaStr = formula.trim();
  if (formulaStr.startsWith("=")) {
    formulaStr = formulaStr.substring(1).trim();
  }
  
  if (!formulaStr) return [];
  
  // Get available columns from schema data if table name is provided
  let availableColumns = [];
  if (tableName) {
    availableColumns = getColumnsFromSchemaData(tableName, isPostgres);
  }
  
  const columnPattern = /\b([A-Za-z_][A-Za-z0-9_]*)\b/g;
  const references = new Set();
  let match;
  
  while ((match = columnPattern.exec(formulaStr)) !== null) {
    const colName = match[1];
    const offset = match.index;
    
    if (isInsideQuotes(formulaStr, offset)) continue;
    
    // Check if it's a SQL keyword
    const isKeyword = SQL_KEYWORDS.has(colName.toUpperCase());
    
    // If it's a keyword, check if it's also a column name in the schema
    if (isKeyword) {
      // Check if this keyword exists as a column in the table
      const existsAsColumn = availableColumns.some((col) => {
        const normalizedCol = col.replace(/\s+/g, "_");
        return normalizedCol === colName || 
               col === colName ||
               normalizedCol.toLowerCase() === colName.toLowerCase() ||
               col.toLowerCase() === colName.toLowerCase();
      });
      
      // If it exists as a column, treat it as a column reference, not a keyword
      if (!existsAsColumn) {
        continue; // Skip as keyword
      }
      // If it exists as a column, continue processing it as a column reference
    }
    
    const afterMatch = formulaStr.substring(offset + colName.length).trim();
    if (afterMatch.startsWith("(")) {
      // This might be a function call
      // Check if it's a known function keyword
      if (SQL_KEYWORDS.has(colName.toUpperCase())) {
        // But if it's also a column, we should still include it
        const existsAsColumn = availableColumns.some((col) => {
          const normalizedCol = col.replace(/\s+/g, "_");
          return normalizedCol === colName || 
                 col === colName ||
                 normalizedCol.toLowerCase() === colName.toLowerCase() ||
                 col.toLowerCase() === colName.toLowerCase();
        });
        if (!existsAsColumn) {
          continue; // Skip as function keyword
        }
      }
    }
    
    if (/^-?\d+(\.\d+)?$/.test(colName)) continue;
    
    references.add(colName);
  }
  
  return Array.from(references);
};

/**
 * Finds the original column name for a normalized column reference
 * 
 * @param {string} normalizedName - The normalized column name (with underscores)
 * @param {Array<string>} availableColumns - Array of available column names to search
 * @returns {string|null} - The original column name if found, null otherwise
 */
const findOriginalColumnName = (normalizedName, availableColumns) => {
  return availableColumns.find((origCol) => {
    const normalizedOrig = origCol.replace(/\s+/g, "_");
    return normalizedOrig === normalizedName || 
           origCol === normalizedName ||
           normalizedOrig.toLowerCase() === normalizedName.toLowerCase() ||
           origCol.toLowerCase() === normalizedName.toLowerCase();
  }) || null;
};

/**
 * Builds the formulaColumnName mapping for a calculated column
 * Maps normalized column names (with underscores) to their original column names from localStorage schema data
 * 
 * @param {string} formula - The Excel formula
 * @param {string} tableName - The table name for the calculated column
 * @param {boolean} isPostgres - Whether to use postgres_tables_data or snowflake_tables_data (default: true)
 * @returns {Array<{NormalizedName: string, originalName: string, dataType?: string}>} - Array of column name mappings
 */
export const buildFormulaColumnNameMapping = (formula, tableName, isPostgres = true) => {
  if (!formula || typeof formula !== "string") return [];
  
  // Pass tableName and isPostgres to extractColumnReferencesFromFormula
  // so it can check if keywords are actually columns
  const columnReferences = extractColumnReferencesFromFormula(formula, tableName, isPostgres);
  if (columnReferences.length === 0) return [];
  
  // Get original column names + types from localStorage schema data
  const originalColumns = getColumnsFromSchemaData(tableName, isPostgres, true);
  
  const formulaColumnName = [];
  
  columnReferences.forEach((normalizedName) => {
    let originalCol = null;
    
    // Find the original column name from schema data
    if (originalColumns.length > 0) {
      originalCol = findOriginalColumnName(
        normalizedName,
        originalColumns.map((c) => c.name)
      );
    }
    
    // If not found in the specified table, search in all tables
    if (!originalCol) {
      try {
        const storageKey = isPostgres ? "postgres_tables_data" : "snowflake_tables_data";
        const tablesDataStr = localStorage.getItem(storageKey);
        
        if (tablesDataStr) {
          const tablesData = JSON.parse(tablesDataStr);
          if (Array.isArray(tablesData)) {
            // Search across all tables
            for (const database of tablesData) {
              if (database.children && Array.isArray(database.children)) {
                for (const table of database.children) {
                  if (table.children && Array.isArray(table.children)) {
                    const tableColumns = table.children
                      .filter((col) => col.type === "column" && col.name)
                      .map((col) => col.name);

                    originalCol = findOriginalColumnName(
                      normalizedName,
                      tableColumns
                    );
                    if (originalCol) break;
                  }
                }
                if (originalCol) break;
              }
            }
          }
        }
      } catch (error) {
        console.error("Error searching across all tables:", error);
      }
    }
    
    // Find datatype for this column, if available
    let dataType = "";
    const matchedMeta =
      originalColumns.find((c) => c.name === originalCol) ||
      originalColumns.find((c) => c.name === normalizedName);
    if (matchedMeta && matchedMeta.textDatatype) {
      dataType = matchedMeta.textDatatype;
    }

    // Add mapping: use original column name if found, otherwise use normalized name
    // (for columns that already have underscores in the database)
    formulaColumnName.push({
      NormalizedName: normalizedName,
      originalName: originalCol || normalizedName,
      dataType: dataType || null,
    });
  });
  
  return formulaColumnName;
};





// // Formula Functions Helper
// // Categorizes all available formulajs functions for easy discovery
 
// import formulajs from "formulajs";
 
// // Get all available function names
// export const getAllFormulaFunctions = () => {
//   return Object.keys(formulajs)
//     .filter((key) => typeof formulajs[key] === "function")
//     .sort();
// };
 
// // Categorize functions for better organization
// export const FORMULA_CATEGORIES = {
//   Mathematical: [
//     "ABS",
//     "CEILING",
//     "CEILINGMATH",
//     "CEILINGPRECISE",
//     "FLOOR",
//     "FLOORMATH",
//     "FLOORPRECISE",
//     "INT",
//     "MOD",
//     "MROUND",
//     "POWER",
//     "PRODUCT",
//     "QUOTIENT",
//     "RAND",
//     "RANDBETWEEN",
//     "ROUND",
//     "ROUNDDOWN",
//     "ROUNDUP",
//     "SIGN",
//     "SQRT",
//     "SQRTPI",
//     "SUM",
//     "SUMIF",
//     "SUMIFS",
//     "SUMPRODUCT",
//     "SUMSQ",
//     "TRUNC",
//   ],
//   Statistical: [
//     "AVERAGE",
//     "AVERAGEA",
//     "AVERAGEIF",
//     "AVERAGEIFS",
//     "AVEDEV",
//     "COUNT",
//     "COUNTA",
//     "COUNTBLANK",
//     "COUNTIF",
//     "COUNTIFS",
//     "COUNTUNIQUE",
//     "CORREL",
//     "COVAR",
//     "COVARIANCE",
//     "COVARIANCEP",
//     "COVARIANCES",
//     "DEVSQ",
//     "FREQUENCY",
//     "GEOMEAN",
//     "HARMEAN",
//     "KURT",
//     "LARGE",
//     "MAX",
//     "MAXA",
//     "MEDIAN",
//     "MIN",
//     "MINA",
//     "MODE",
//     "MODEMULT",
//     "MODESNGL",
//     "PERCENTILE",
//     "PERCENTILEEXC",
//     "PERCENTILEINC",
//     "PERCENTRANK",
//     "PERCENTRANKEXC",
//     "PERCENTRANKINC",
//     "QUARTILE",
//     "QUARTILEEXC",
//     "QUARTILEINC",
//     "RANK",
//     "RANKAVG",
//     "RANKEQ",
//     "SKEW",
//     "SKEWP",
//     "SMALL",
//     "STDEV",
//     "STDEVA",
//     "STDEVP",
//     "STDEVPA",
//     "STDEVS",
//     "STEYX",
//     "TRIMMEAN",
//     "VAR",
//     "VARA",
//     "VARP",
//     "VARPA",
//     "VARS",
//   ],
//   Trigonometric: [
//     "ACOS",
//     "ACOSH",
//     "ACOT",
//     "ACOTH",
//     "ASIN",
//     "ASINH",
//     "ATAN",
//     "ATAN2",
//     "ATANH",
//     "COS",
//     "COSH",
//     "COT",
//     "COTH",
//     "CSC",
//     "CSCH",
//     "DEGREES",
//     "RADIANS",
//     "SEC",
//     "SECH",
//     "SIN",
//     "SINH",
//     "TAN",
//     "TANH",
//   ],
//   Logical: [
//     "AND",
//     "FALSE",
//     "IF",
//     "IFERROR",
//     "IFNA",
//     "NOT",
//     "OR",
//     "SWITCH",
//     "TRUE",
//     "XOR",
//   ],
//   Text: [
//     "CHAR",
//     "CLEAN",
//     "CODE",
//     "CONCATENATE",
//     "DOLLAR",
//     "EXACT",
//     "FIND",
//     "FIXED",
//     "LEFT",
//     "LEN",
//     "LOWER",
//     "MID",
//     "PROPER",
//     "REPLACE",
//     "REPT",
//     "RIGHT",
//     "SEARCH",
//     "SUBSTITUTE",
//     "TEXT",
//     "TRIM",
//     "UPPER",
//     "VALUE",
//   ],
//   "Date & Time": [
//     "DATE",
//     "DATEVALUE",
//     "DAY",
//     "DAYS",
//     "DAYS360",
//     "EDATE",
//     "EOMONTH",
//     "HOUR",
//     "ISOWEEKNUM",
//     "MINUTE",
//     "MONTH",
//     "NETWORKDAYS",
//     "NETWORKDAYSINTL",
//     "NOW",
//     "SECOND",
//     "TIME",
//     "TIMEVALUE",
//     "TODAY",
//     "WEEKDAY",
//     "WEEKNUM",
//     "WORKDAY",
//     "WORKDAYINTL",
//     "YEAR",
//     "YEARFRAC",
//   ],
//   Financial: [
//     "ACCRINT",
//     "ACCRINTM",
//     "AMORDEGRC",
//     "AMORLINC",
//     "COUPDAYBS",
//     "COUPDAYS",
//     "COUPDAYSNC",
//     "COUPNCD",
//     "COUPNUM",
//     "COUPPCD",
//     "CUMIPMT",
//     "CUMPRINC",
//     "DB",
//     "DDB",
//     "DISC",
//     "DOLLARDE",
//     "DOLLARFR",
//     "DURATION",
//     "EFFECT",
//     "FV",
//     "FVSCHEDULE",
//     "INTRATE",
//     "IPMT",
//     "IRR",
//     "ISPMT",
//     "MDURATION",
//     "MIRR",
//     "NOMINAL",
//     "NPER",
//     "NPV",
//     "PDURATION",
//     "PMT",
//     "PPMT",
//     "PRICE",
//     "PRICEDISC",
//     "PRICEMAT",
//     "PV",
//     "RATE",
//     "RECEIVED",
//     "RRI",
//     "SLN",
//     "SYD",
//     "TBILLEQ",
//     "TBILLPRICE",
//     "TBILLYIELD",
//     "VDB",
//     "XIRR",
//     "XNPV",
//     "YIELD",
//     "YIELDDISC",
//     "YIELDMAT",
//   ],
//   "Lookup & Reference": [
//     "CHOOSE",
//     "COLUMN",
//     "COLUMNS",
//     "MATCH",
//     "ROW",
//     "ROWS",
//     "TRANSPOSE",
//   ],
//   Information: [
//     "CELL",
//     "ERROR",
//     "INFO",
//     "ISBINARY",
//     "ISBLANK",
//     "ISERR",
//     "ISERROR",
//     "ISEVEN",
//     "ISFORMULA",
//     "ISLOGICAL",
//     "ISNA",
//     "ISNONTEXT",
//     "ISNUMBER",
//     "ISODD",
//     "ISPMT",
//     "ISREF",
//     "ISTEXT",
//     "N",
//     "NA",
//     "TYPE",
//   ],
//   Engineering: [
//     "BESSELI",
//     "BESSELJ",
//     "BESSELK",
//     "BESSELY",
//     "BIN2DEC",
//     "BIN2HEX",
//     "BIN2OCT",
//     "COMPLEX",
//     "CONVERT",
//     "DEC2BIN",
//     "DEC2HEX",
//     "DEC2OCT",
//     "DELTA",
//     "ERF",
//     "ERFC",
//     "ERFCPRECISE",
//     "ERFPRECISE",
//     "GESTEP",
//     "HEX2BIN",
//     "HEX2DEC",
//     "HEX2OCT",
//     "IMABS",
//     "IMAGINARY",
//     "IMARGUMENT",
//     "IMCONJUGATE",
//     "IMCOS",
//     "IMCOSH",
//     "IMCOT",
//     "IMCSC",
//     "IMCSCH",
//     "IMDIV",
//     "IMEXP",
//     "IMLN",
//     "IMLOG10",
//     "IMLOG2",
//     "IMPOWER",
//     "IMPRODUCT",
//     "IMREAL",
//     "IMSEC",
//     "IMSECH",
//     "IMSIN",
//     "IMSINH",
//     "IMSQRT",
//     "IMSUB",
//     "IMSUM",
//     "IMTAN",
//     "OCT2BIN",
//     "OCT2DEC",
//     "OCT2HEX",
//   ],
//   Database: [
//     "DAVERAGE",
//     "DCOUNT",
//     "DCOUNTA",
//     "DGET",
//     "DMAX",
//     "DMIN",
//     "DPRODUCT",
//     "DSTDEV",
//     "DSTDEVP",
//     "DSUM",
//     "DVAR",
//     "DVARP",
//   ],
//   Other: [
//     "AGGREGATE",
//     "ARABIC",
//     "BASE",
//     "BETA",
//     "BETADIST",
//     "BETAINV",
//     "BINOM",
//     "BINOMDIST",
//     "BITAND",
//     "BITLSHIFT",
//     "BITOR",
//     "BITRSHIFT",
//     "BITXOR",
//     "CHIDIST",
//     "CHIDISTRT",
//     "CHIINV",
//     "CHIINVRT",
//     "CHISQ",
//     "CHITEST",
//     "COMBIN",
//     "COMBINA",
//     "CONFIDENCE",
//     "CRITBINOM",
//     "DECIMAL",
//     "EXP",
//     "EXPON",
//     "EXPONDIST",
//     "F",
//     "FACT",
//     "FACTDOUBLE",
//     "FDIST",
//     "FDISTRT",
//     "FINV",
//     "FINVRT",
//     "FISHER",
//     "FISHERINV",
//     "FORECAST",
//     "FTEST",
//     "GAMMA",
//     "GAMMADIST",
//     "GAMMAINV",
//     "GAMMALN",
//     "GAMMALNPRECISE",
//     "GAUSS",
//     "GCD",
//     "GROWTH",
//     "HYPGEOM",
//     "HYPGEOMDIST",
//     "INTERCEPT",
//     "INTERVAL",
//     "LCM",
//     "LINEST",
//     "LN",
//     "LOG",
//     "LOG10",
//     "LOGEST",
//     "LOGINV",
//     "LOGNORM",
//     "LOGNORMDIST",
//     "LOGNORMINV",
//     "MDETERM",
//     "MINVERSE",
//     "MMULT",
//     "MULTINOMIAL",
//     "MUNIT",
//     "NEGBINOM",
//     "NEGBINOMDIST",
//     "NORM",
//     "NORMDIST",
//     "NORMINV",
//     "NORMSDIST",
//     "NORMSINV",
//     "ODD",
//     "ODDFPRICE",
//     "ODDFYIELD",
//     "ODDLPRICE",
//     "ODDLYIELD",
//     "PEARSON",
//     "PERMUT",
//     "PERMUTATIONA",
//     "PHI",
//     "POISSON",
//     "POISSONDIST",
//     "POW",
//     "PROB",
//     "RSQ",
//     "SERIESSUM",
//     "SLOPE",
//     "STANDARDIZE",
//     "SUMX2MY2",
//     "SUMX2PY2",
//     "SUMXMY2",
//     "SUBTOTAL",
//     "TDIST",
//     "TDISTRT",
//     "TINV",
//     "TODAY",
//     "TREND",
//     "TTEST",
//     "UNICHAR",
//     "UNICODE",
//     "UNIQUE",
//     "WEIBULL",
//     "WEIBULLDIST",
//     "Z",
//     "ZTEST",
//   ],
// };
 
// // Get function description/syntax (simplified)
// export const getFunctionSyntax = (funcName) => {
//   const syntaxMap = {
//     SUM: "SUM(number1, number2, ...)",
//     AVERAGE: "AVERAGE(number1, number2, ...)",
//     IF: "IF(condition, value_if_true, value_if_false)",
//     ROUND: "ROUND(number, num_digits)",
//     MAX: "MAX(number1, number2, ...)",
//     MIN: "MIN(number1, number2, ...)",
//     COUNT: "COUNT(value1, value2, ...)",
//     SUMIF: "SUMIF(range, criteria, [sum_range])",
//     COUNTIF: "COUNTIF(range, criteria)",
//     VLOOKUP:
//       "VLOOKUP(lookup_value, table_array, col_index_num, [range_lookup])",
//     CONCATENATE: "CONCATENATE(text1, text2, ...)",
//     LEFT: "LEFT(text, [num_chars])",
//     RIGHT: "RIGHT(text, [num_chars])",
//     LEN: "LEN(text)",
//     UPPER: "UPPER(text)",
//     LOWER: "LOWER(text)",
//     TRIM: "TRIM(text)",
//     DATE: "DATE(year, month, day)",
//     NOW: "NOW()",
//     TODAY: "TODAY()",
//     YEAR: "YEAR(date)",
//     MONTH: "MONTH(date)",
//     DAY: "DAY(date)",
//     AND: "AND(logical1, logical2, ...)",
//     OR: "OR(logical1, logical2, ...)",
//     NOT: "NOT(logical)",
//     SQRT: "SQRT(number)",
//     POWER: "POWER(number, power)",
//     LOG: "LOG(number, [base])",
//     LN: "LN(number)",
//     EXP: "EXP(number)",
//     SIN: "SIN(number)",
//     COS: "COS(number)",
//     TAN: "TAN(number)",
//     ABS: "ABS(number)",
//     FLOOR: "FLOOR(number, significance)",
//     CEILING: "CEILING(number, significance)",
//     MOD: "MOD(number, divisor)",
//     ROUNDUP: "ROUNDUP(number, num_digits)",
//     ROUNDDOWN: "ROUNDDOWN(number, num_digits)",
//   };
 
//   return syntaxMap[funcName] || `${funcName}(...)`;
// };
 
// // Get all functions in a flat list with category
// export const getAllFunctionsWithCategory = () => {
//   const functions = [];
//   Object.entries(FORMULA_CATEGORIES).forEach(([category, funcNames]) => {
//     funcNames.forEach((funcName) => {
//       functions.push({
//         name: funcName,
//         category: category,
//         syntax: getFunctionSyntax(funcName),
//       });
//     });
//   });
//   return functions.sort((a, b) => a.name.localeCompare(b.name));
// };
 

